import { Injectable, inject, signal, computed } from '@angular/core';
import { Observable, of, fromEvent, merge, Subject, Subscription, interval } from 'rxjs';
import { tap, map, catchError, finalize, switchMap } from 'rxjs/operators';

import { AppStatusService } from '@core/services';
import { TimeoutConfig } from '@core/types';

type IdleState = 'active' | 'warning';

@Injectable({ providedIn: 'root' })
export class IdleStore {

  private readonly appStatusService = inject(AppStatusService);

  private readonly _config = signal<TimeoutConfig | null>(null);
  private readonly _configLoading = signal<boolean>(false);
  private readonly _configError = signal<string | null>(null);

  private idleState: IdleState = 'active';

  private readonly _countdown = signal(0);
  private readonly _isWarning = signal(false);

  private activitySub?: Subscription;
  private idleTimer?: any;
  private countdownInterval?: any;
  private keepAliveSub?: Subscription;

  readonly config = computed(() => this._config());
  readonly configLoading = computed(() => this._configLoading());
  readonly configError = computed(() => this._configError());

  readonly countdown = computed(() => this._countdown());
   readonly formattedCountdown = computed(() => {
    const totalSeconds = this._countdown();

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  });
  
  readonly isWarning = computed(() => this._isWarning());

  readonly isConfigReady = computed(() => !!this._config());

  private readonly warningSubject = new Subject<void>();
  readonly warning$ = this.warningSubject.asObservable();

  private readonly expiredSubject = new Subject<void>();
  readonly expired$ = this.expiredSubject.asObservable();

  loadConfig(): Observable<TimeoutConfig | null> {
    this._configLoading.set(true);
    this._configError.set(null);

    return this.appStatusService.getTimeoutConfig().pipe(
      tap(res => {
        if (res.data) {
          this._config.set({...res.data, sessionKeepAliveIntervalInSeconds: 300});
        }
      }),
      map(res => res.data),
      finalize(() => {
        this._configLoading.set(false);
      })
    );
  }

  start(): void {
    const config = this._config();
    if (!config) {
      console.warn('[Idle] Cannot start - config not loaded');
      return;
    }

    this.stop();

    this.idleState = 'active';
    this._isWarning.set(false);

    this.startActivityTracking(config);
    this.startKeepAlivePing(config);
    this.resetIdleTimer(config);
  }

  stop(): void {

    this.activitySub?.unsubscribe();
    this.keepAliveSub?.unsubscribe();
    clearTimeout(this.idleTimer);
    clearInterval(this.countdownInterval);

    this._countdown.set(0);
    this._isWarning.set(false);
    this.idleState = 'active';
  }

  private startActivityTracking(config: TimeoutConfig): void {

    const activity$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'click'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'touchstart')
    );

    this.activitySub = activity$.subscribe(() => {

      if (this.idleState === 'active') {
        this.resetIdleTimer(config);
      }
    });
  }

  private startKeepAlivePing(config: TimeoutConfig): void {
    this.keepAliveSub?.unsubscribe();

    const pingIntervalMs = config.sessionKeepAliveIntervalInSeconds * 1000;


    if (pingIntervalMs <= 0) {
      return;
    }

    this.keepAliveSub = interval(pingIntervalMs)
      .pipe(
        switchMap(() => {
          return this.appStatusService.ping();
        }),
      )
      .subscribe();
  }

  private resetIdleTimer(config: TimeoutConfig): void {

    clearTimeout(this.idleTimer);

    const idleMs = config.autoLogOut * 1000;

    this.idleTimer = setTimeout(() => {
      this.enterWarning(config);
    }, idleMs);
  }

  private enterWarning(config: TimeoutConfig): void {

    this.idleState = 'warning';
    this._isWarning.set(true);

    this.activitySub?.unsubscribe();

    this.warningSubject.next();

    const start = Date.now();
    const warningMs = config.autoLogOutWarning * 1000;


    this.countdownInterval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = warningMs - elapsed;

      if (remaining <= 0) {

        clearInterval(this.countdownInterval);
        this.expiredSubject.next();
      } else {
        const seconds = Math.ceil(remaining / 1000);

        this._countdown.set(seconds);
      }
    }, 1000);
  }

  stayLoggedIn(): void {

    if (this.idleState !== 'warning') {
      return;
    }

    const config = this._config();
    if (!config) {
      return;
    }

    clearInterval(this.countdownInterval);

    this._countdown.set(0);
    this._isWarning.set(false);
    this.idleState = 'active';


    this.startActivityTracking(config);
    this.resetIdleTimer(config);
  }

  forceExpire(): void {

    clearInterval(this.countdownInterval);
    this.expiredSubject.next();
  }

  reset(): void {

    this._config.set(null);
    this._configLoading.set(false);
    this._configError.set(null);

    this.stop();
  }
}